package com.ijoin.data.api

import com.ijoin.data.api.ApiClient.addAuthToken
import com.ijoin.data.model.CancelEventRequest
import com.ijoin.data.model.Event
import com.ijoin.data.model.EventCapacity
import com.ijoin.data.model.EventCapacityBatchRequest
import com.ijoin.data.model.EventWizardRequest
import com.ijoin.data.model.PageResponse
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.datetime.Instant

class EventApi {
    private val client = ApiClient.client
    private val baseUrl = "${ApiClient.getBaseUrl()}/api/events"

    suspend fun getUpcomingEvents(page: Int = 0, size: Int = 20): Result<List<Event>> {
        return safeApiCall {
            client.get("$baseUrl/upcoming") {
                addAuthToken()
                parameter("page", page)
                parameter("size", size)
            }
        }.mapCatching { response ->
            val pageResponse: PageResponse<Event> = response.body()
            pageResponse.content
        }
    }

    suspend fun searchEvents(query: String, page: Int = 0, size: Int = 20): Result<List<Event>> {
        return safeApiCall {
            client.get("${ApiClient.getBaseUrl()}/api/search/events") {
                addAuthToken()
                parameter("q", query)
                parameter("page", page)
                parameter("size", size)
            }
        }.mapCatching { response ->
            val pageResponse: PageResponse<Event> = response.body()
            pageResponse.content
        }
    }

    suspend fun getEventById(eventId: String): Result<Event> {
        return safeApiCall {
            client.get("$baseUrl/$eventId") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun getOccurrenceCapacity(eventId: String, occurrenceDateTime: Instant): Result<EventCapacity> {
        return safeApiCall {
            client.get("$baseUrl/$eventId/occurrences/capacity") {
                addAuthToken()
                parameter("occurrence", occurrenceDateTime.toString())
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun getOccurrencesCapacity(eventId: String, occurrences: List<Instant>): Result<List<EventCapacity>> {
        if (occurrences.isEmpty()) {
            return Result.success(emptyList())
        }
        val request = EventCapacityBatchRequest(occurrences)
        return safeApiCall {
            client.post("$baseUrl/$eventId/occurrences/capacity/batch") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun getFavoriteEvents(): Result<List<Event>> {
        return safeApiCall {
            client.get("$baseUrl/favorites") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun addToFavorites(eventId: String): Result<Unit> {
        return safeApiCall {
            client.post("$baseUrl/$eventId/favorite") {
                addAuthToken()
            }
        }.map { }
    }

    suspend fun removeFromFavorites(eventId: String): Result<Unit> {
        return safeApiCall {
            client.delete("$baseUrl/$eventId/favorite") {
                addAuthToken()
            }
        }.map { }
    }

    suspend fun isFavorite(eventId: String): Result<Boolean> {
        return safeApiCall {
            client.get("$baseUrl/$eventId/favorite") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun createEventFromWizard(payload: EventWizardRequest): Result<Event> {
        return safeApiCall {
            client.post("$baseUrl/wizard") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(payload)
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun updateEventFromWizard(eventId: String, payload: EventWizardRequest): Result<Event> {
        return safeApiCall {
            client.put("$baseUrl/$eventId/wizard") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(payload)
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun cancelEvent(eventId: String, reason: String?): Result<Event> {
        val request = CancelEventRequest(reason = reason?.takeIf { it.isNotBlank() })
        return safeApiCall {
            client.post("$baseUrl/$eventId/cancel") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun restoreEvent(eventId: String): Result<Event> {
        return safeApiCall {
            client.post("$baseUrl/$eventId/restore") {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    suspend fun deleteEvent(eventId: String): Result<Unit> {
        return safeApiCall {
            client.delete("$baseUrl/$eventId") {
                addAuthToken()
            }
        }.map { }
    }
}
